// Firebase Analytics Service for Capacitor
// Handles analytics for native mobile apps and bridges web gtag
import { Capacitor } from '@capacitor/core';

class FirebaseService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.analytics = null;
    this.crashlytics = null;
    this.isInitialized = false;
  }

  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      if (this.isNative) {
        // Native Firebase Analytics
        const { FirebaseAnalytics } = await import('@capacitor-firebase/analytics');
        const { FirebaseCrashlytics } = await import('@capacitor-firebase/crashlytics');
        
        this.analytics = FirebaseAnalytics;
        this.crashlytics = FirebaseCrashlytics;
        
        // Enable analytics collection
        await FirebaseAnalytics.setCollectionEnabled({ enabled: true });
        
        // Enable crashlytics collection
        await FirebaseCrashlytics.setEnabled({ enabled: true });
        
        console.log('Firebase native SDK initialized');
      } else {
        // Web: use gtag (already loaded in index.html)
        console.log('Firebase web SDK (gtag) initialized');
      }
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize Firebase:', error);
    }
  }

  // Log custom events
  async logEvent(eventName, params = {}) {
    try {
      if (this.isNative && this.analytics) {
        await this.analytics.logEvent({ name: eventName, params });
      } else if (window.gtag) {
        window.gtag('event', eventName, params);
      }
    } catch (error) {
      console.error('Failed to log event:', error);
    }
  }

  // Log screen view
  async logScreenView(screenName, screenClass = 'App') {
    try {
      if (this.isNative && this.analytics) {
        await this.analytics.setCurrentScreen({ 
          screenName, 
          screenClassOverride: screenClass 
        });
      } else if (window.gtag) {
        window.gtag('event', 'screen_view', {
          screen_name: screenName,
          screen_class: screenClass,
        });
      }
    } catch (error) {
      console.error('Failed to log screen view:', error);
    }
  }

  // Set user properties
  async setUserProperty(name, value) {
    try {
      if (this.isNative && this.analytics) {
        await this.analytics.setUserProperty({ name, value });
      } else if (window.gtag) {
        window.gtag('set', 'user_properties', { [name]: value });
      }
    } catch (error) {
      console.error('Failed to set user property:', error);
    }
  }

  // Log Quran-specific events
  async logSurahView(surahId, surahName) {
    await this.logEvent('surah_view', {
      surah_id: surahId,
      surah_name: surahName,
    });
  }

  async logAudioPlay(surahId, reciter) {
    await this.logEvent('audio_play', {
      surah_id: surahId,
      reciter: reciter,
    });
  }

  async logBookmarkAdd(verseKey) {
    await this.logEvent('bookmark_add', {
      verse_key: verseKey,
    });
  }

  async logTranslationChange(translationId) {
    await this.logEvent('translation_change', {
      translation_id: translationId,
    });
  }

  async logLanguageChange(language) {
    await this.logEvent('language_change', {
      language: language,
    });
  }

  // Crashlytics methods
  async logError(message, error) {
    try {
      if (this.isNative && this.crashlytics) {
        await this.crashlytics.log({ message });
        if (error) {
          await this.crashlytics.recordException({ 
            message: error.message || String(error) 
          });
        }
      }
      console.error(message, error);
    } catch (e) {
      console.error('Failed to log error to crashlytics:', e);
    }
  }

  async setUserId(userId) {
    try {
      if (this.isNative) {
        if (this.analytics) {
          await this.analytics.setUserId({ userId });
        }
        if (this.crashlytics) {
          await this.crashlytics.setUserId({ userId });
        }
      } else if (window.gtag) {
        window.gtag('set', { user_id: userId });
      }
    } catch (error) {
      console.error('Failed to set user ID:', error);
    }
  }
}

export const firebaseService = new FirebaseService();
export default firebaseService;
